import { useResizeObserver } from '@react-hookz/web/useResizeObserver/index.js';
import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { IconSystem } from '#icons/IconSystem';

import styles from './ForecastSection.module.scss';

type Props = {
	title: ReactNode;
	className?: string;
};

export function ForecastSection({ title, className, children }: PropsWithChildren<Props>) {
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const previousPositionRef = useRef<number>(0);
	// const [scrollable, setScrollable] = useState(false);
	const [scrollButtonPrevVisible, setScrollButtonPrevVisible] = useState<boolean>(false);
	const [scrollButtonNextVisible, setScrollButtonNextVisible] = useState<boolean>(false);
	const prevButtonId = `wpb-${useId()}`;
	const nextButtonId = `wnb-${useId()}`;

	// control the scroll snap behavior
	useEffect(() => {
		if (scrollContainerRef.current) {
			const scrollContainer = scrollContainerRef.current;

			function scrollHandler() {
				if (scrollContainer) {
					const left = scrollContainer.scrollLeft > 0;
					const right = scrollContainer.scrollLeft + scrollContainer.clientWidth < scrollContainer.scrollWidth;

					setScrollButtonPrevVisible(left);
					setScrollButtonNextVisible(right);

					previousPositionRef.current = scrollContainer.scrollLeft;
				}
			}

			scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });

			return () => scrollContainer.removeEventListener('scroll', scrollHandler);
		} else {
			return undefined;
		}
	}, []);

	const scrollTo = useCallback((direction: -1 | 1) => {
		// scroll by half the scroll size
		const container = scrollContainerRef.current;
		if (container) {
			container.scrollBy({ left: (direction * container.clientWidth) / 2, behavior: 'smooth' });
		}
	}, []);

	useResizeObserver(
		scrollContainerRef,
		(event: ResizeObserverEntry) => {
			const container = event.target;
			if (container) {
				const left = container.scrollLeft > 0;
				const right = container.scrollLeft + container.clientWidth < container.scrollWidth;

				setScrollButtonPrevVisible(left);
				setScrollButtonNextVisible(right);
			}
		},
		true
	);

	return (
		<section className={className ?? ''}>
			<h2 className="heading3">{title}</h2>
			<div className={`${styles.detailsContainer}`}>
				<button
					className={`${styles.scrollButton} ${scrollButtonPrevVisible ? styles.enabled : ''} button-link`}
					title="Nach links scrollen"
					onClick={() => {
						scrollTo(-1);
					}}
					id={prevButtonId}
				>
					<IconSystem icon="chevron-left" variant="filled" aria-labelledby={prevButtonId} />
				</button>
				<div className={`${styles.details}`} ref={scrollContainerRef}>
					{children}
				</div>
				<button
					className={`${styles.scrollButton} ${scrollButtonNextVisible ? styles.enabled : ''} button-link`}
					title="Nach rechts scrollen"
					onClick={() => {
						scrollTo(1);
					}}
					id={nextButtonId}
				>
					<IconSystem icon="chevron-right" variant="filled" aria-labelledby={nextButtonId} />
				</button>
			</div>
		</section>
	);
}
